import './Issue.scss';
import { Grid, useMediaQuery } from '@mui/material';
import { KeyboardEvent, useState } from 'react';
import { useTheme } from '@mui/system';
import DetailsDialog from './DetailsDialog/DetailsDialog';
import StatusBadge from '../StatusBadge/StatusBadge';
import { useTranslation } from 'react-i18next';

interface Props {
  issue: any;
  issuesIndex: number;
  staticContent: any;
}

const Issue = ({ issue, issuesIndex, staticContent }: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const {
    affected_api,
    details,
    environment,
    error_message,
    last_updated,
    summary,
    targeted_fix_date,
    use_case,
    workaround
  } = issue;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'TBC';
    const locale = t('issue.formatDate');
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    const formattedMonth = date.toLocaleString(locale, { month: 'long' });

    return locale === 'en-US'
      ? `${formattedMonth} ${day}, ${year}`
      : `${day} ${formattedMonth} ${year}`;
  };

  const getHeaderClassName = (index: number) =>
    index === 0 ? 'issue-header' : 'issue-header issue-header--mt';

  const getIssueClassName = () =>
    issuesIndex % 2 === 0 ? 'issue' : 'issue background-alco-sno';

  const handleOnClickViewDetails = () => {
    setShowDetailsDialog(true);
  };

  const handleOnKeyDownCloseViewDetails = (
    event: KeyboardEvent<SVGSVGElement>
  ) => {
    if (event.key === 'Enter') {
      setShowDetailsDialog(false);
    }
  };

  const handleOnKeyDownViewDetails = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setShowDetailsDialog(true);
    }
  };

  const col1Fields = {
    affected_api,
    summary
  };

  const col2Fields = {
    environment,
    last_updated: formatDate(last_updated),
    targeted_fix_date: formatDate(targeted_fix_date)
  };

  const detailFields = {
    ...col1Fields,
    details,
    use_case,
    error_message,
    workaround,
    ...col2Fields
  };

  return (
    <>
      <Grid
        className={getIssueClassName()}
        container
        mt={issuesIndex === 0 ? 40 : 0}
        spacing={2}
      >
        <Grid
          item
          display={isDesktop ? 'block' : 'flex'}
          justifyContent="space-between"
          md={2}
          xs={12}
        >
          <h1 className="issue-title">{issue.ticket_id}</h1>
          <StatusBadge
            status={issue.status}
            statuses={staticContent.statuses}
            parentComponentName="Known Issues"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {Object.entries(col1Fields).map(([key, value], index) => {
            return (
              <Grid item xs={12} key={key}>
                <div className={getHeaderClassName(index)}>
                  {staticContent.fields[key]}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: Array.isArray(value)
                      ? value.map((item) => item.value).join(', ')
                      : value
                  }}
                />
              </Grid>
            );
          })}
          <div
            className="issue-header--link"
            onClick={() => handleOnClickViewDetails()}
            onKeyDown={(e) => handleOnKeyDownViewDetails(e)}
            tabIndex={0}
          >
            {staticContent.viewDetails}
          </div>
        </Grid>
        <Grid item xs={12} md={4}>
          {Object.entries(col2Fields).map(([key, value], index) => {
            return (
              <Grid item xs={12} key={key}>
                <div className={getHeaderClassName(index)}>
                  {staticContent.fields[key]}
                </div>
                <div dangerouslySetInnerHTML={{ __html: value }} />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <DetailsDialog
        issueId={issue.ticket_id}
        fields={detailFields}
        open={showDetailsDialog}
        staticContent={staticContent}
        status={issue.status}
        handleOnClose={() => setShowDetailsDialog(false)}
        handleOnKeyDown={(e) => handleOnKeyDownCloseViewDetails(e)}
      />
    </>
  );
};

export default Issue;
